import React from 'react';
import { Box } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  REGION_COLORS, REGION_LABELS, formatPrice,
  useContainerWidth, EmptyState, CHART_MARGINS_SIMPLE, defaultLineLegendProps, tickLabelStyle,
} from './shared';

/**
 * Price Duration Curve
 *
 * Classic energy market tool: prices sorted descending, showing what % of time
 * the price exceeds each level. One line per NEM region.
 */
const PriceDurationChart = ({ data, regions }) => {
  const { ref, width } = useContainerWidth();

  if (!data || data.length === 0) return <EmptyState message="No price duration data available" />;

  // Group by region
  const byRegion = {};
  data.forEach(({ region_id, percentile, price }) => {
    if (!byRegion[region_id]) byRegion[region_id] = [];
    byRegion[region_id].push({ percentile, price });
  });

  const regionIds = Object.keys(byRegion).sort();
  const allPercentiles = [...new Set(data.map((d) => d.percentile))].sort((a, b) => a - b);
  const xLabels = allPercentiles.map((p) => `${p.toFixed(0)}%`);

  const series = regionIds.map((rid) => {
    const regionData = byRegion[rid];
    const pctMap = {};
    regionData.forEach(({ percentile, price }) => { pctMap[percentile] = price; });

    return {
      type: 'line',
      label: REGION_LABELS[rid] || rid,
      data: allPercentiles.map((p) => pctMap[p] ?? null),
      color: REGION_COLORS[rid] || '#6b7280',
      curve: 'monotone',
      connectNulls: true,
    };
  });

  return (
    <Box ref={ref} sx={{ width: '100%', height: 420, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <LineChart
          width={width}
          height={420}
          series={series}
          xAxis={[{
            scaleType: 'point',
            data: xLabels,
            label: '% of time price is exceeded',
            tickLabelStyle,
            tickNumber: 20,
          }]}
          yAxis={[{
            valueFormatter: formatPrice,
            label: 'Price (A$/MWh)',
            tickLabelStyle,
          }]}
          margin={CHART_MARGINS_SIMPLE}
          slotProps={{ legend: { ...defaultLineLegendProps } }}
        />
      )}
    </Box>
  );
};

export default PriceDurationChart;
